import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult({ kind }) {
  const [state, setState] = useState(kind === "success" ? "verifying" : "cancelled");

  useEffect(() => {
    if (kind !== "success") return;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        /* retry */
      }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [kind]);

  const content = {
    verifying: {
      icon: <Loader2 className="h-10 w-10 animate-spin text-emerald-400" />,
      title: "Confirmation du paiement…",
      text: "Nous vérifions votre transaction auprès de Stripe. Quelques secondes suffisent.",
    },
    paid: {
      icon: <CheckCircle2 className="h-10 w-10 text-emerald-400" />,
      title: "Paiement confirmé !",
      text: "Bienvenue à bord. Nous vous contactons sous 4h ouvrables pour lancer la création de votre site.",
    },
    pending: {
      icon: <Loader2 className="h-10 w-10 animate-spin text-emerald-400" />,
      title: "Paiement en cours de traitement",
      text: "Votre banque finalise la transaction. Vous recevrez une confirmation par courriel.",
    },
    cancelled: {
      icon: <XCircle className="h-10 w-10 text-red-400" />,
      title: "Paiement annulé",
      text: "Aucun montant n'a été débité. Vous pouvez reprendre quand vous voulez.",
    },
    error: {
      icon: <XCircle className="h-10 w-10 text-red-400" />,
      title: "Session introuvable",
      text: "Impossible de retrouver cette transaction. Contactez-nous au +1 438 969 6957.",
    },
  }[state];

  return (
    <div
      className="grid-bg flex min-h-screen items-center justify-center px-4"
      data-testid={`payment-${kind}-page`}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="glass emerald-glow w-full max-w-md rounded-2xl p-10 text-center"
      >
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/5" data-testid="payment-status-icon">
          {content.icon}
        </div>
        <h1 className="mt-6 text-2xl font-bold text-white" data-testid="payment-status-title">
          {content.title}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-gray-400" data-testid="payment-status-text">
          {content.text}
        </p>
        <a
          href={state === "cancelled" ? "/#tarifs" : "/"}
          data-testid="payment-home-button"
          className="mt-8 inline-flex items-center justify-center rounded-full bg-emerald-500 px-7 py-3 text-sm font-semibold text-emerald-950 transition-colors hover:bg-emerald-400"
        >
          {state === "cancelled" ? "Retour aux forfaits" : "Retour à l'accueil"}
        </a>
      </motion.div>
    </div>
  );
}
